import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { getEmptyImage, HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { usePreview } from 'react-dnd-preview';
import GTM from '@mirinae/apis/GoogleAnalytics';

import { fontkoKR } from '@mirinae/react-ui';
import { os } from '@mirinae/js-utils/utils/userAgent';

import PlayerContext from '@mirinae/lessons/modules/contexts/player';
import { lessonsPalette } from '@mirinae/lessons/modules/defines/styles';
import { getFields } from '@mirinae/lessons/modules/libs/elementLib';
import { FormattedKoEnPhrase } from '@mirinae/lessons/components/lesson/elements/Phrase';
import { ReactComponent as ArrowSVG } from '@mirinae/lessons/components/svg/img-arrow.svg';
import SuccessOverlay from '@mirinae/lessons/components/lesson/elements/shared/SuccessOverlay';
import Element from '@mirinae/lessons/components/lesson/elements/Element';

const Wrapper = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
    align-items: center;
    width: 100%;
    min-height: 175px;

    ${!os.isOthers && `
        flex-direction: column;
        width: 100%;
    `}
`;

const SuccessBanner = styled(SuccessOverlay)`
    position: absolute;
    top: -30px;
`;

const ChoiceBox = styled.div`
    font-family: ${fontkoKR};
    color: ${props => (props.isOver ? lessonsPalette.darken[0] : lessonsPalette.darken[1])};
    font-size: ${props => (props.isOver ? '48px' : '36px')};
    width: 200px;
    height: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const MobileChoiceBox = styled.div`
    font-family: ${fontkoKR};
    color: ${props => (props.isOver ? lessonsPalette.darken[0] : lessonsPalette.darken[1])};
    font-size: ${props => (props.isOver ? '34px' : '36px')};
    width: 150px;
    height: 100px;
    display: flex;
    align-items: center;
    font-weight: bold;

    &:first-child {
        justify-content: flex-start;
        padding-left: 10px;
    }
    &:last-child {
        justify-content: flex-end;
        padding-right: 10px;
    }
`;

const MobileChoicesRow = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    flex-spacing: center;
    justify-content: space-between;
    width: 100%;
`;

const MobileArrowRow = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: center;
    width: 100%;
`;

const ParticleContainer = styled.div`
    width: 120px;
    height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: ${props => (props.wiggle ? 'wiggle 0.15s' : '')};
    animation-delay: 150ms;
    user-select: none;

    @keyframes wiggle {
        0% { transform: translate(0%); }
        5% { transform: translate(5%); }
        50% { transform: translate(-5%); }
        75% { transform: translate(5%); }
        95% { transform: translate(-5%); }
        100% { transform: translate(0%); }
    }
`;

const ParticleCircle = styled.div`
    font-size: 28px;
    background: ${lessonsPalette.primary.white};
    border: ${props => (props.badPick ? `1px solid ${lessonsPalette.lessons[2]}` : `1px solid ${lessonsPalette.grey[5]}`)};
    box-shadow: ${props => (props.badPick ? 'reset' : '3px 3px 7px rgba(159, 188, 186, 0.4)')};
    background-color: ${props => (props.badPick ? lessonsPalette.lessons[3] : lessonsPalette.primary.white)};
    border-radius: 50%;
    height: 100px;
    width: 100px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    visibility: ${props => (props.isDragging ? 'hidden' : 'visible')};
    user-select: none;
    transition: ease-out width .1s, height .1s;
 
    :hover {
        height: 120px;
        width: 120px;
        box-shadow: unset;
        border: 1px solid ${lessonsPalette.darken[0]};
        background-color: ${lessonsPalette.lessons[4]};
    }
`;

const ParticleCirclePreview = styled(ParticleCircle)`
    height: 120px;
    width: 120px;
    box-shadow: unset;
    border: 1px solid ${lessonsPalette.darken[0]};
    background-color: #EEF6F5;

    ${!os.isOthers && `
        height: 102px;
        width: 102px;
    `}
`;

const FinalForm = styled.div`
    animation: answerfade 1.5s linear forwards;

    @keyframes answerfade {
        0% { opacity: 0.2 }
        95% { opacity: 0.2 }
        100% { opacity: 1 }
    }
`;

const ArrowLeft = styled(ArrowSVG)`
    transform: rotate(180deg);

    ${!os.isOthers && `
        transform: translateX(-42px) translateY(20px) rotate(-120deg);
    `}
`;

const ArrowRight = styled(ArrowSVG)`
    ${!os.isOthers && `
        transform: translateX(42px) translateY(20px) rotate(-60deg);
    `}
`;

const Particle = ({ text, canDrag, badPick }) => {
    const [{ isDragging }, drag, preview] = useDrag(() => ({
        type: 'particle',
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging(),
        }),
        canDrag: () => canDrag,
    }));

    useEffect(() => {
        preview(getEmptyImage(), { captureDraggingState: false });
    }, []);

    return (
        <ParticleCircle
            badPick={badPick}
            isDragging={isDragging}
            ref={drag}
        >
            {text}
        </ParticleCircle>
    );
};

const Choice = ({ onDrop, text, boxType }) => {
    const [{ isOver }, drop] = useDrop(() => ({
        accept: 'particle',
        drop: onDrop,
        collect: monitor => ({
            isOver: !!monitor.isOver(),
        }),
    }), []);

    const Box = boxType === 'mobile' ? MobileChoiceBox : ChoiceBox;
    
    return (
        <Box 
            isOver={isOver} 
            ref={drop}
        >
            {text}
        </Box>
    );
};

const Drag = ({ element }) => {
    const {
        left,
        right,
        particle,
        correctDirection,
    } = getFields(
        element,
        'left',
        'right',
        'particle',
        'correctDirection',
    );

    const player = useContext(PlayerContext);
    const [complete, setComplete] = useState(false);
    const [badPick, setBadPick] = useState(false);

    const onDrop = (direction) => {
        if (direction === correctDirection) {
            setComplete(true);
            player.postEvent({ action: 'complete' }, element);
            GTM.userAction('success', { page: player.currentPage.componentName }, 'lessons');
        } else {
            setBadPick(true);
            player.postEvent({ action: 'error' }, element);
            GTM.userAction('error', { page: player.currentPage.componentName }, 'lessons');
        }
    };

    const DragPreview = ({ text }) => {
        const { display, style } = usePreview();
        if (!display) {
            return null;
        }
        return (
            <ParticleCirclePreview style={style}>
                <span>{text}</span>
            </ParticleCirclePreview>
        );
    };

    // mobile and desktop require differently ordered layouts
    // as the easiest way to solve some styling requirements.
    const renderMobile = () => (
        <>
            <MobileChoicesRow>
                <Choice
                    onDrop={() => onDrop('left')} 
                    text={left}
                    boxType="mobile"
                />
                <Choice 
                    onDrop={() => onDrop('right')} 
                    text={right}
                    boxType="mobile"
                />
            </MobileChoicesRow>
            <MobileArrowRow>
                <ArrowLeft />
                <ArrowRight />
            </MobileArrowRow>
            <ParticleContainer onMouseDown={() => setBadPick(false)} wiggle={badPick}>
                <Particle badPick={badPick} text={particle} canDrag={!complete} />
            </ParticleContainer> 
        </>
    );

    const renderDesktop = () => (
        <>
            <Choice
                onDrop={() => onDrop('left')} 
                text={left}
            />
            <ArrowLeft />
            <ParticleContainer onMouseDown={() => setBadPick(false)} wiggle={badPick}>
                <Particle badPick={badPick} text={particle} canDrag={!complete} />
            </ParticleContainer> 
            <ArrowRight />
            <Choice 
                onDrop={() => onDrop('right')} 
                text={right}
            />
        </>
    );

    return (
        <Element element={element}>
            <DndProvider backend={os.isOthers ? HTML5Backend : TouchBackend}>
                <Wrapper>
                    {complete ? <SuccessBanner /> : null}
                    {complete 
                        ? (
                            <FinalForm>
                                <FormattedKoEnPhrase phrase={element.fieldsByName.finalForm} phraseStyle={element.style} />
                            </FinalForm>
                        )
                        : (
                            <>
                                {os.isOthers ? renderDesktop() : renderMobile()}
                            </>
                        )}
                    {complete ? null : <DragPreview text={particle} />}
                </Wrapper>
            </DndProvider>
        </Element>
    );
};

export default Drag;
